/**
 * AltTextGenButton - Sparkles button that generates alt-text for an image card.
 */
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useGenerateAltText } from './useGenerateAltText';

export interface AltTextGenButtonProps {
  productId: string;
  assetId: string;
  onGenerated: (text: string) => void;
}

export function AltTextGenButton({ productId, assetId, onGenerated }: AltTextGenButtonProps) {
  const { t } = useTranslation();
  const { generate, loading } = useGenerateAltText();
  const label = t('aiField.generateAltText', 'Generate alt text');

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = await generate(productId, assetId);
    if (text) onGenerated(text);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      aria-label={label}
      title={label}
      className={cn(
        'absolute bottom-2 left-9 z-20',
        'flex items-center justify-center w-6 h-6 rounded',
        'bg-black/70 text-white hover:bg-primary-600 transition-colors',
        'opacity-0 group-hover/card:opacity-100',
        'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-400',
        loading && 'opacity-60 cursor-wait'
      )}
    >
      <Sparkles className={cn('h-3.5 w-3.5', loading && 'animate-pulse')} />
    </button>
  );
}
